import React from 'react';
import { 
  Calendar, MapPin, AlertCircle, Bike
} from 'lucide-react';

// Custom motorbike icon component
const MotorbikeIcon = ({ size = 16, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M8 5h8l2 4h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1a3 3 0 0 1-3 3 3 3 0 0 1-3-3H7a3 3 0 0 1-3 3 3 3 0 0 1-3-3V7a2 2 0 0 1 2-2h5zm0 9a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm8 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"></path>
  </svg>
);

/**
 * RentalDetailsSection Component
 * Handles rendering of rental details fields (pickup/dropoff dates, locations, motorbike selection)
 * Two-step bike selection: Model → Registration Number
 * Includes bike availability checking
 * NOW WITH DELIVERY LOCATIONS INTEGRATION
 */
const RentalDetailsSection = ({
  reservationData,
  handleEnhancedInputChange,
  fieldValidation,
  reservationSettings,
  isSaving,
  uploadingDocuments,
  selectedModel,
  setSelectedModel,
  motorbikes,
  bikeStatus,
  checkingAvailability,
  editIndex,
  handleInputChange
}) => {
  
  // ===================================
  // DELIVERY LOCATIONS HELPER FUNCTIONS
  // ===================================
  
  // Get all enabled delivery locations
  const getEnabledLocations = () => {
    const locations = Array.isArray(reservationSettings.deliveryLocations) 
      ? reservationSettings.deliveryLocations 
      : [];
    return locations.filter(loc => loc && loc.enabled === true);
  };

  // Get pickup locations (category 'pickup' or 'both')
  const getPickupLocations = () => {
    const enabledLocations = getEnabledLocations();
    return enabledLocations.filter(loc => 
      loc.category === 'pickup' || loc.category === 'both'
    );
  };

  // Get dropoff locations (category 'dropoff' or 'both')
  const getDropoffLocations = () => {
    const enabledLocations = getEnabledLocations();
    return enabledLocations.filter(loc => 
      loc.category === 'dropoff' || loc.category === 'both'
    );
  };


  // ===================================
  // BIKE SELECTION HELPER FUNCTIONS
  // ===================================
  
  // Get icon component for field
  const getFieldIcon = (iconName) => {
    const icons = {
      Calendar,
      MapPin,
      Bike: MotorbikeIcon
    };
    return icons[iconName] || Calendar;
  };

  // Get unique bike models
  const getUniqueModels = () => {
    const models = [...new Set(motorbikes.map(bike => bike.model || 'Unknown'))];
    return models.filter(Boolean).sort();
  };

  // Get bikes filtered by model
  const getBikesByModel = (model) => {
    if (!model) return [];
    return motorbikes.filter(bike => bike.model === model);
  };

  // Handle model selection
  const handleModelChange = (model) => {
    setSelectedModel(model);
    if (handleInputChange) {
      handleInputChange({
        target: {
          name: 'motorbikeNumber',
          value: ''
        }
      });
    }
  };

  // ===================================
  // FIELD RENDERING FUNCTIONS
  // ===================================

  // Get rental details fields from settings
  const getRentalDetailsFields = () => {
    const fieldSettings = reservationSettings.fieldSettings || {};
    const rentalFieldKeys = ['pickupDateTime', 'dropoffDateTime', 'pickupLocation', 'dropoffLocation', 'motorbikeNumber'];
    
    return Object.entries(fieldSettings)
      .filter(([fieldKey, fieldConfig]) => 
        fieldConfig && 
        fieldConfig.visible && 
        rentalFieldKeys.includes(fieldKey)
      )
      .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));
  };

  // Render individual rental details field
  const renderRentalField = (fieldKey, fieldConfig) => {
    if (!fieldConfig || !fieldConfig.visible) return null;

    const value = reservationData[fieldKey] || '';
    const validation = fieldValidation[fieldKey];
    const hasError = validation && !validation.isValid;
    const IconComponent = getFieldIcon(fieldConfig.icon);

    const commonProps = {
      name: fieldKey,
      value: value,
      onChange: handleEnhancedInputChange,
      disabled: isSaving || uploadingDocuments,
      required: fieldConfig.required,
      className: `w-full p-3 bg-gray-700 border text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 transition-colors ${
        hasError ? 'border-red-500 bg-red-900/20' : 'border-gray-600'
      } ${reservationSettings.formBehavior?.compactMode ? 'p-2 text-sm' : 'p-3'}`,
      placeholder: fieldConfig.placeholder || `Enter ${fieldConfig.label.toLowerCase()}`
    };

    const renderFieldInput = () => {
      switch (fieldKey) {
        case 'pickupDateTime':
        case 'dropoffDateTime':
          return (
            <input
              type="datetime-local"
              {...commonProps}
            />
          );

        case 'pickupLocation':
          const pickupLocations = getPickupLocations();
          const hasPickupLocations = pickupLocations.length > 0;
          
          return (
            <div>
              <select {...commonProps}>
                <option value="">
                  {hasPickupLocations ? 'Select pickup location...' : 'No pickup locations available'}
                </option>
                {pickupLocations.map((location) => (
                  <option key={location.id} value={location.name}>
                    {location.name}
                    {reservationSettings.enableDeliveryCharges && parseFloat(location.deliveryCharge) > 0 
                      ? ` (+$${parseFloat(location.deliveryCharge).toFixed(2)})` 
                      : ''}
                  </option>
                ))}
              </select>
              
              {/* ✅ CHANGED: selected location description box removed */}
              
              {/* No locations warning */}
              {!hasPickupLocations && (
                <div className="mt-2 bg-orange-900/20 border border-orange-700 rounded-lg p-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={14} className="text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-orange-300 text-xs font-medium">No Pickup Locations Available</p>
                      <p className="text-orange-400/70 text-xs mt-0.5">
                        Please configure pickup locations in Settings → Locations
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );

        case 'dropoffLocation':
          const dropoffLocations = getDropoffLocations();
          const hasDropoffLocations = dropoffLocations.length > 0;
          
          return (
            <div>
              <select {...commonProps}>
                <option value="">
                  {hasDropoffLocations ? 'Select drop-off location...' : 'No drop-off locations available'}
                </option>
                {dropoffLocations.map((location) => (
                  <option key={location.id} value={location.name}>
                    {location.name}
                    {reservationSettings.enableDeliveryCharges && parseFloat(location.deliveryCharge) > 0 
                      ? ` (+$${parseFloat(location.deliveryCharge).toFixed(2)})` 
                      : ''}
                  </option>
                ))}
              </select>
              
              {/* ✅ CHANGED: selected location description box removed */}
              
              {/* No locations warning */}
              {!hasDropoffLocations && (
                <div className="mt-2 bg-orange-900/20 border border-orange-700 rounded-lg p-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={14} className="text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-orange-300 text-xs font-medium">No Drop-off Locations Available</p>
                      <p className="text-orange-400/70 text-xs mt-0.5">
                        Please configure drop-off locations in Settings → Locations
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );

        case 'motorbikeNumber':
          const uniqueModels = getUniqueModels();
          const filteredBikes = getBikesByModel(selectedModel);
          
          return (
            // ✅ CHANGED: same gap as the main grid, so the two selects line up with the columns above
            <div className={`grid grid-cols-1 md:grid-cols-2 ${reservationSettings.formBehavior?.compactMode ? 'gap-4' : 'gap-6'}`}>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  1. Select Bike Model
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => handleModelChange(e.target.value)}
                  disabled={isSaving || uploadingDocuments}
                  className={`w-full p-3 bg-gray-700 border text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 transition-colors border-gray-600 ${
                    reservationSettings.formBehavior?.compactMode ? 'p-2 text-sm' : 'p-3'
                  }`}
                >
                  <option value="">Select Model First</option>
                  {uniqueModels.map((model, index) => (
                    <option key={index} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  2. Select Registration Number
                </label>
                <select 
                  {...commonProps}
                  disabled={!selectedModel || isSaving || uploadingDocuments}
                  className={`${commonProps.className} ${
                    !bikeStatus.isAvailable && editIndex === null ? 'border-red-500' : ''
                  }`}
                >
                  <option value="">
                    {!selectedModel ? 'Select a model first' : 'Select Registration Number'}
                  </option>
                  {filteredBikes.map((bike, index) => {
                    const bikeReg = bike.regNumber || bike.registration || bike;
                    const dailyRate = bike.dailyRate ? ` - $${bike.dailyRate}/day` : '';
                    const status = bike.status ? ` (${bike.status})` : '';
                    
                    return (
                      <option key={bike.id || index} value={bikeReg}>
                        {bikeReg}{dailyRate}{status}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          );

        default:
          return (
            <input
              type="text"
              {...commonProps}
            />
          );
      }
    };

    return (
      <div
        key={fieldKey}
        className={`${reservationSettings.formBehavior?.compactMode ? 'space-y-1' : 'space-y-2'} ${
          // ✅ CHANGED: Motorbike field takes the full row width (both columns)
          fieldKey === 'motorbikeNumber' ? 'md:col-span-2' : ''
        }`}
      >
        <label className="block text-sm font-medium text-gray-300">
          <div className="flex items-center gap-2">
            {reservationSettings.formBehavior?.showFieldIcons && (
              <IconComponent size={16} className="text-orange-400" />
            )}
            <span>
              {fieldConfig.label} 
              {fieldConfig.required && <span className="text-red-500 ml-1">*</span>}
            </span>
            
            {(fieldKey === 'pickupDateTime' || fieldKey === 'dropoffDateTime') && (
              <span className="text-xs text-blue-400">(Past dates allowed)</span>
            )}
            {fieldKey === 'motorbikeNumber' && checkingAvailability && (
              <span className="text-xs text-blue-400">(Checking availability...)</span>
            )}
          </div>
          
          {reservationSettings.formBehavior?.showFieldDescriptions && fieldConfig.description && (
            <div className="text-xs text-gray-500 mt-1 font-normal">
              {fieldConfig.description}
            </div>
          )}
        </label>
        
        {renderFieldInput()}
        
        {hasError && validation.errors.length > 0 && (
          <div className="space-y-1">
            {validation.errors.map((error, index) => (
              <div key={index} className="text-red-400 text-xs flex items-center gap-1">
                <AlertCircle size={12} />
                <span>{error}</span>
              </div>
            ))}
          </div>
        )}
        
        {fieldKey === 'motorbikeNumber' && !bikeStatus.isAvailable && editIndex === null && (
          <div className="text-red-400 text-xs flex items-center gap-1 mt-1">
            <AlertCircle size={12} />
            <span>{bikeStatus.message}</span>
          </div>
        )}
      </div>
    );
  };

  const rentalFields = getRentalDetailsFields();

  // Don't render section if no fields are visible
  if (rentalFields.length === 0) return null;

  return (
    <div className={reservationSettings.formBehavior?.compactMode ? 'space-y-3' : 'space-y-4'}>
      {/* Section Header */}
      {reservationSettings.formBehavior?.groupRelatedFields && (
        <div className="border-b border-gray-700 pb-2">
          <h4 className="text-lg font-semibold text-white flex items-center gap-2">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            Rental Details
          </h4>
          {/* ✅ CHANGED: "5 fields • Dates, locations, and bike selection" line removed */}
        </div>
      )}
      
      {/* Rental Fields Grid */}
      <div className={`grid gap-${reservationSettings.formBehavior?.compactMode ? '4' : '6'} ${
        reservationSettings.formBehavior?.compactMode 
          ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
          : 'grid-cols-1 md:grid-cols-2'
      }`}>
        {rentalFields.map(([fieldKey, fieldConfig]) => renderRentalField(fieldKey, fieldConfig))}
      </div>

    </div>
  );
};

export default RentalDetailsSection;